<template>
  <div class="report-catalog-page">
    <!-- Header -->
    <div class="report-header d-flex align-center justify-space-between px-3 px-sm-6 py-3">
      <div class="d-flex align-center" style="gap: 12px;">
        <v-btn icon @click="$router.push({ name: 'auth', params: { authPage: 'shop' } })">
          <v-icon>mdi-arrow-left</v-icon>
        </v-btn>
        <h2 class="mb-0 primary--text font-weight-bold" style="font-size: 20px;">
          <v-icon color="primary" class="mr-1" style="vertical-align: middle;">mdi-package-variant-closed</v-icon>
          Catalog Report
        </h2>
      </div>
      <div class="d-flex align-center" style="gap: 12px;">
        <v-btn
          v-if="isSaleManager"
          depressed
          color="primary"
          class="manage-team-btn"
          style="text-transform: none;"
          @click="$router.push({ name: 'report-catalog-manage' })"
        >
          <v-icon small left>mdi-account-group</v-icon>
          Manage Sellers
        </v-btn>
        <span class="grey--text text--darken-1 d-none d-sm-inline" style="font-size: 13px;"
          >Catalog Performance Dashboard</span
        >
      </div>
    </div>

    <v-divider />

    <!-- Body -->
    <div class="report-body pa-3 pa-sm-5">
      <v-row no-gutters style="gap: 0; align-items: flex-start;">
        <!-- ===== LEFT: Catalog Performance ===== -->
        <v-col cols="12" lg="6" class="pr-lg-4 mb-5 mb-lg-0">
          <!-- ===== Date filter (global) ===== -->
          <v-card rounded="lg" class="pa-4 mb-4 date-filter-card" elevation="1">
            <div class="d-flex align-center justify-space-between" style="gap: 12px; flex-wrap: wrap;">
              <div class="d-flex align-center" style="gap: 8px;">
                <v-icon color="primary">mdi-calendar</v-icon>
                <span class="font-weight-bold primary--text" style="font-size: 15px;">Report Date</span>
              </div>
              <div class="d-flex align-center" style="gap: 8px; flex-wrap: wrap;">
                <v-btn small outlined color="primary" style="text-transform: none;" @click="goPrevDay">
                  <v-icon small>mdi-chevron-left</v-icon>
                </v-btn>
                <v-menu
                  v-model="dateMenu"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                  min-width="290px"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      v-bind="attrs"
                      v-on="on"
                      small
                      outlined
                      color="primary"
                      style="min-width: 220px; text-transform: none;"
                    >
                      <v-icon small left>mdi-calendar-today</v-icon>
                      {{ formatDisplayDate(selectedDate) }}
                    </v-btn>
                  </template>
                  <v-date-picker
                    v-model="selectedDate"
                    no-title
                    scrollable
                    :max="today"
                    @input="
                      dateMenu = false;
                      refreshAll();
                    "
                  />
                </v-menu>
                <v-btn
                  small
                  outlined
                  color="primary"
                  style="text-transform: none;"
                  :disabled="selectedDate >= today"
                  @click="goNextDay"
                >
                  <v-icon small>mdi-chevron-right</v-icon>
                </v-btn>
                <v-btn small text color="primary" style="text-transform: none;" @click="goToday">
                  <v-icon small left>mdi-calendar-today-outline</v-icon>
                  Today
                </v-btn>
                <v-btn depressed color="primary" :loading="isLoadingAll" @click="refreshAll">
                  <v-icon small left>mdi-refresh</v-icon>
                  Apply
                </v-btn>
              </div>
            </div>
          </v-card>

          <div ref="leftContent">
            <!-- Capture-only banner: identify who took the screenshot -->
            <div v-if="capture.isCapturing" class="capture-user-banner mb-3">
              <div class="capture-user-banner__avatar">
                <v-icon size="20" color="white">mdi-account-circle</v-icon>
              </div>
              <div class="capture-user-banner__text">
                <div class="capture-user-banner__name">{{ captureUserName }}</div>
                <div class="capture-user-banner__email">{{ captureUserEmail }}</div>
              </div>
              <div class="capture-user-banner__meta">
                <div class="capture-user-banner__date">
                  <v-icon size="14" color="#3f51b5">mdi-calendar-today</v-icon>
                  {{ formatDisplayDate(selectedDate) }}
                </div>
                <div class="capture-user-banner__time">
                  <v-icon size="13" color="#888">mdi-clock-outline</v-icon>
                  Captured at {{ captureTimestamp }}
                </div>
              </div>
            </div>

            <!-- ===== Overall KPI Summary (All Platforms) ===== -->
            <div class="d-flex align-center justify-space-between mb-2">
              <span class="font-weight-bold grey--text text--darken-3 d-flex align-center" style="font-size: 14px;">
                <v-icon color="primary" small class="mr-1">mdi-chart-areaspline</v-icon>
                Overall KPI Summary
                <v-chip x-small label color="primary" dark class="ml-2 font-weight-bold">All Platforms</v-chip>
              </span>
            </div>
            <v-row no-gutters class="mb-4" style="gap: 0;">
              <v-col cols="12" sm="6" class="pr-sm-2 mb-2 mb-sm-0">
                <v-card rounded="lg" class="pa-3 summary-card summary-card--day summary-card--compact" elevation="1">
                  <div class="d-flex align-center mb-2" style="gap: 6px;">
                    <div class="summary-card__icon summary-card__icon--day summary-card__icon--sm">
                      <v-icon size="16" color="white">mdi-calendar-today</v-icon>
                    </div>
                    <div class="summary-card__head-text">
                      <div class="summary-card__title summary-card__title--sm">Daily Total</div>
                      <div class="summary-card__subtitle">{{ formatDisplayDate(selectedDate) }}</div>
                    </div>
                  </div>
                  <div class="summary-grid summary-grid--compact">
                    <div class="summary-cell summary-cell--orders">
                      <span class="summary-cell__label">Orders</span>
                      <span class="summary-cell__value">{{
                        Number(catalog.summaryAll.totalOrder || 0).toLocaleString()
                      }}</span>
                    </div>
                    <div class="summary-cell summary-cell--spent">
                      <span class="summary-cell__label">Spent</span>
                      <span class="summary-cell__value">{{ formatCurrency(catalog.summaryAll.totalSpent) }}</span>
                    </div>
                    <div class="summary-cell summary-cell--rev">
                      <span class="summary-cell__label">Revenue</span>
                      <span class="summary-cell__value">{{ formatCurrency(catalog.summaryAll.totalRev) }}</span>
                    </div>
                    <div class="summary-cell" :class="roasChipClass(catalog.summaryAll.roas)">
                      <span class="summary-cell__label">ROAS</span>
                      <span class="summary-cell__value" :class="roasTextClass(catalog.summaryAll.roas)">
                        {{ Number(catalog.summaryAll.roas || 0).toFixed(2) }}x
                      </span>
                    </div>
                    <div class="summary-cell summary-cell--spent">
                      <span class="summary-cell__label">Profit Seller</span>
                      <span class="summary-cell__value">{{ formatCurrency(catalog.summaryAll.profitSeller) }}</span>
                    </div>
                  </div>
                </v-card>
              </v-col>
              <v-col cols="12" sm="6" class="pl-sm-2">
                <v-card rounded="lg" class="pa-3 summary-card summary-card--month summary-card--compact" elevation="1">
                  <div class="d-flex align-center mb-2" style="gap: 6px;">
                    <div class="summary-card__icon summary-card__icon--month summary-card__icon--sm">
                      <v-icon size="16" color="white">mdi-calendar-month</v-icon>
                    </div>
                    <div class="summary-card__head-text">
                      <div class="summary-card__title summary-card__title--sm">Monthly Total</div>
                      <div class="summary-card__subtitle">{{ monthLabel(selectedDate) }}</div>
                    </div>
                  </div>
                  <div class="summary-grid summary-grid--compact">
                    <div class="summary-cell summary-cell--orders">
                      <span class="summary-cell__label">Orders</span>
                      <span class="summary-cell__value">{{
                        Number(catalog.monthlySummaryAll.totalOrder || 0).toLocaleString()
                      }}</span>
                    </div>
                    <div class="summary-cell summary-cell--spent">
                      <span class="summary-cell__label">Spent</span>
                      <span class="summary-cell__value">{{
                        formatCurrency(catalog.monthlySummaryAll.totalSpent)
                      }}</span>
                    </div>
                    <div class="summary-cell summary-cell--rev">
                      <span class="summary-cell__label">Revenue</span>
                      <span class="summary-cell__value">{{ formatCurrency(catalog.monthlySummaryAll.totalRev) }}</span>
                    </div>
                    <div class="summary-cell" :class="roasChipClass(catalog.monthlySummaryAll.roas)">
                      <span class="summary-cell__label">ROAS</span>
                      <span class="summary-cell__value" :class="roasTextClass(catalog.monthlySummaryAll.roas)">
                        {{ Number(catalog.monthlySummaryAll.roas || 0).toFixed(2) }}x
                      </span>
                    </div>
                    <div class="summary-cell summary-cell--spent">
                      <span class="summary-cell__label">Profit Seller</span>
                      <span class="summary-cell__value">{{
                        formatCurrency(catalog.monthlySummaryAll.profitSeller)
                      }}</span>
                    </div>
                  </div>
                </v-card>
              </v-col>
            </v-row>

            <!-- ===== Single Master Platform Tab Bar ===== -->
            <v-card rounded="lg" class="pa-2 mb-4 platform-tabs-card no-capture" elevation="1">
              <v-tabs
                v-model="catalog.platformTab"
                background-color="transparent"
                color="primary"
                grow
                active-class="font-weight-bold"
              >
                <v-tab class="text-capitalize">
                  <v-icon left small>mdi-storefront-outline</v-icon>
                  Dropnoxe
                </v-tab>
                <v-tab class="text-capitalize">
                  <v-icon left small>mdi-shopify</v-icon>
                  Shopify
                </v-tab>
              </v-tabs>
            </v-card>

            <!-- ===== Platform-specific KPI Summary ===== -->
            <div class="d-flex align-center justify-space-between mb-2">
              <span class="font-weight-bold grey--text text--darken-2" style="font-size: 13px;">
                {{ catalog.platformTab === 1 ? 'Shopify' : 'Dropnoxe' }} KPI Summary
              </span>
            </div>
            <v-row no-gutters class="mb-4" style="gap: 0;">
              <v-col cols="12" sm="6" class="pr-sm-2 mb-2 mb-sm-0">
                <v-card rounded="lg" class="pa-3 summary-card summary-card--day summary-card--compact" elevation="1">
                  <div class="d-flex align-center mb-2" style="gap: 6px;">
                    <div class="summary-card__icon summary-card__icon--day summary-card__icon--sm">
                      <v-icon size="16" color="white">mdi-calendar-today</v-icon>
                    </div>
                    <div class="summary-card__head-text">
                      <div class="summary-card__title summary-card__title--sm">
                        Daily ({{ catalog.platformTab === 1 ? 'Shopify' : 'Dropnoxe' }})
                      </div>
                      <div class="summary-card__subtitle">{{ formatDisplayDate(selectedDate) }}</div>
                    </div>
                  </div>
                  <div class="summary-grid summary-grid--compact">
                    <div class="summary-cell summary-cell--orders">
                      <span class="summary-cell__label">Orders</span>
                      <span class="summary-cell__value">{{
                        Number(activeDailySummary.totalOrder || 0).toLocaleString()
                      }}</span>
                    </div>
                    <div class="summary-cell summary-cell--spent">
                      <span class="summary-cell__label">Spent</span>
                      <span class="summary-cell__value">{{ formatCurrency(activeDailySummary.totalSpent) }}</span>
                    </div>
                    <div class="summary-cell summary-cell--rev">
                      <span class="summary-cell__label">Revenue</span>
                      <span class="summary-cell__value">{{ formatCurrency(activeDailySummary.totalRev) }}</span>
                    </div>
                    <div class="summary-cell" :class="roasChipClass(activeDailySummary.roas)">
                      <span class="summary-cell__label">ROAS</span>
                      <span class="summary-cell__value" :class="roasTextClass(activeDailySummary.roas)">
                        {{ Number(activeDailySummary.roas || 0).toFixed(2) }}x
                      </span>
                    </div>
                    <div v-if="catalog.platformTab === 0" class="summary-cell summary-cell--spent">
                      <span class="summary-cell__label">Profit Seller</span>
                      <span class="summary-cell__value">{{ formatCurrency(activeDailySummary.profitSeller) }}</span>
                    </div>
                  </div>
                </v-card>
              </v-col>
              <v-col cols="12" sm="6" class="pl-sm-2">
                <v-card rounded="lg" class="pa-3 summary-card summary-card--month summary-card--compact" elevation="1">
                  <div class="d-flex align-center mb-2" style="gap: 6px;">
                    <div class="summary-card__icon summary-card__icon--month summary-card__icon--sm">
                      <v-icon size="16" color="white">mdi-calendar-month</v-icon>
                    </div>
                    <div class="summary-card__head-text">
                      <div class="summary-card__title summary-card__title--sm">
                        Monthly ({{ catalog.platformTab === 1 ? 'Shopify' : 'Dropnoxe' }})
                      </div>
                      <div class="summary-card__subtitle">{{ monthLabel(selectedDate) }}</div>
                    </div>
                  </div>
                  <div class="summary-grid summary-grid--compact">
                    <div class="summary-cell summary-cell--orders">
                      <span class="summary-cell__label">Orders</span>
                      <span class="summary-cell__value">{{
                        Number(activeMonthlySummary.totalOrder || 0).toLocaleString()
                      }}</span>
                    </div>
                    <div class="summary-cell summary-cell--spent">
                      <span class="summary-cell__label">Spent</span>
                      <span class="summary-cell__value">{{ formatCurrency(activeMonthlySummary.totalSpent) }}</span>
                    </div>
                    <div class="summary-cell summary-cell--rev">
                      <span class="summary-cell__label">Revenue</span>
                      <span class="summary-cell__value">{{ formatCurrency(activeMonthlySummary.totalRev) }}</span>
                    </div>
                    <div class="summary-cell" :class="roasChipClass(activeMonthlySummary.roas)">
                      <span class="summary-cell__label">ROAS</span>
                      <span class="summary-cell__value" :class="roasTextClass(activeMonthlySummary.roas)">
                        {{ Number(activeMonthlySummary.roas || 0).toFixed(2) }}x
                      </span>
                    </div>
                    <div v-if="catalog.platformTab === 0" class="summary-cell summary-cell--spent">
                      <span class="summary-cell__label">Profit Seller</span>
                      <span class="summary-cell__value">{{ formatCurrency(activeMonthlySummary.profitSeller) }}</span>
                    </div>
                  </div>
                </v-card>
              </v-col>
            </v-row>

            <!-- ===== Daily catalog table ===== -->
            <v-card
              ref="catalogCard"
              rounded="lg"
              class="pa-4 catalog-card mb-4"
              :class="{ capturing: capture.isCapturing }"
              elevation="1"
            >
              <div class="d-flex align-center justify-space-between mb-4" style="gap: 12px; flex-wrap: wrap;">
                <h3 class="primary--text font-weight-bold mb-0 d-flex align-center" style="font-size: 15px;">
                  <v-icon color="primary" small class="mr-1">mdi-calendar-today</v-icon>
                  Daily Catalog ({{ catalog.platformTab === 1 ? 'Shopify' : 'Dropnoxe' }})
                  <span class="catalog-card__period">{{ formatDisplayDate(selectedDate) }}</span>
                  <v-chip
                    v-if="activeDailyList.length"
                    x-small
                    label
                    color="primary"
                    outlined
                    class="ml-2 font-weight-bold"
                  >
                    {{ activeDailyList.length }}
                  </v-chip>
                </h3>
                <div class="d-flex align-center" style="gap: 6px; flex-wrap: wrap;">
                  <v-tooltip top>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn
                        v-bind="attrs"
                        v-on="on"
                        depressed
                        small
                        color="success"
                        :loading="capture.isCapturing"
                        :disabled="catalog.isLoading"
                        class="no-capture"
                        @click="captureCatalog"
                      >
                        <v-icon small>mdi-camera</v-icon>
                      </v-btn>
                    </template>
                    <span>Capture full catalog as image</span>
                  </v-tooltip>
                  <v-btn
                    depressed
                    small
                    color="primary"
                    :loading="catalog.isLoading"
                    :disabled="capture.isCapturing"
                    class="no-capture"
                    @click="fetchCatalog(true)"
                  >
                    <v-icon small>mdi-refresh</v-icon>
                  </v-btn>
                </div>
              </div>

              <!-- Table -->
              <div class="table-scroll-wrapper">
                <v-data-table
                  :headers="catalogHeaders"
                  :items="activeDailyList"
                  :loading="catalog.isLoading"
                  :items-per-page="-1"
                  hide-default-footer
                  item-key="catalog"
                  class="report-table"
                >
                  <!-- Image -->
                  <template v-slot:[`item.image`]="{ item }">
                    <div class="catalog-thumb-wrap">
                      <v-img
                        v-if="getCatalogImage(item)"
                        :src="imageHelpers.url(getCatalogImage(item), { width: 80, height: 80 })"
                        width="48"
                        height="48"
                        class="rounded catalog-thumb"
                        contain
                      />
                      <div v-else class="catalog-thumb-placeholder">
                        <v-icon color="grey lighten-1">mdi-image-off-outline</v-icon>
                      </div>
                    </div>
                  </template>

                  <!-- Name -->
                  <template v-slot:[`item.name`]="{ item }">
                    <v-tooltip top max-width="320">
                      <template v-slot:activator="{ on, attrs }">
                        <div v-bind="attrs" v-on="on" class="cell-clamp font-weight-medium primary--text">
                          {{ getCatalogName(item) }}
                        </div>
                      </template>
                      <span>{{ getCatalogName(item) }}</span>
                    </v-tooltip>
                  </template>

                  <!-- Spent -->
                  <template v-slot:[`item.totalSpent`]="{ item }">
                    <span class="red--text text--darken-1 font-weight-medium">{{
                      formatCurrency(item.totalSpent)
                    }}</span>
                  </template>

                  <!-- Revenue -->
                  <template v-slot:[`item.totalRev`]="{ item }">
                    <span class="green--text text--darken-2 font-weight-medium">{{
                      formatCurrency(item.totalRev)
                    }}</span>
                  </template>

                  <!-- ROAS -->
                  <template v-slot:[`item.roas`]="{ item }">
                    <span class="font-weight-medium" :class="roasTextClass(item.roas)">
                      {{ Number(item.roas || 0).toFixed(2) }}x
                    </span>
                  </template>

                  <!-- Order -->
                  <template v-slot:[`item.totalOrder`]="{ item }">
                    <div class="order-cell">
                      <span class="order-cell__num">{{ (item.totalOrder || 0).toLocaleString() }}</span>
                    </div>
                  </template>

                  <!-- Note (display + edit toggle) -->
                  <template v-slot:[`item.note`]="{ item }">
                    <div class="note-cell">
                      <!-- Display mode (also forced during capture) -->
                      <div v-if="!item._isEditing || capture.isCapturing" class="note-display-wrap">
                        <div
                          class="note-display"
                          :class="{ 'note-display--empty': !item.note }"
                          @click="enterEditNote(item)"
                        >
                          <span v-if="item.note" class="note-display__text">{{ item.note }}</span>
                          <span v-else class="note-display__placeholder">— add note —</span>
                        </div>
                        <v-tooltip v-if="!capture.isCapturing" top>
                          <template v-slot:activator="{ on, attrs }">
                            <v-btn
                              v-bind="attrs"
                              v-on="on"
                              icon
                              x-small
                              class="note-action-btn no-capture"
                              color="primary"
                              :loading="!!item._noteSaving"
                              @click="enterEditNote(item)"
                            >
                              <v-icon size="18">
                                {{ item._noteSaved ? 'mdi-check-circle' : 'mdi-pencil-outline' }}
                              </v-icon>
                            </v-btn>
                          </template>
                          <span>{{ item.note ? 'Edit note' : 'Add note' }}</span>
                        </v-tooltip>
                      </div>

                      <!-- Edit mode -->
                      <div v-else class="note-edit">
                        <v-textarea
                          v-model="item.note"
                          :ref="`noteInput_${item.catalog || item._id}`"
                          placeholder="Type note here…"
                          hide-details
                          dense
                          outlined
                          rows="2"
                          auto-grow
                          no-resize
                          class="note-input"
                          :disabled="!!item._noteSaving"
                          @keydown.enter.exact.prevent="saveNoteAndExit(item)"
                          @keydown.esc="cancelEditNote(item)"
                          @blur="onNoteBlur(item)"
                        />
                        <div class="note-edit__actions">
                          <v-tooltip top>
                            <template v-slot:activator="{ on, attrs }">
                              <v-btn
                                v-bind="attrs"
                                v-on="on"
                                icon
                                x-small
                                color="success"
                                :loading="!!item._noteSaving"
                                @click="saveNoteAndExit(item)"
                              >
                                <v-icon size="18">mdi-check</v-icon>
                              </v-btn>
                            </template>
                            <span>Save (Enter)</span>
                          </v-tooltip>
                          <v-tooltip top>
                            <template v-slot:activator="{ on, attrs }">
                              <v-btn
                                v-bind="attrs"
                                v-on="on"
                                icon
                                x-small
                                color="grey darken-1"
                                :disabled="!!item._noteSaving"
                                @click="cancelEditNote(item)"
                              >
                                <v-icon size="18">mdi-close</v-icon>
                              </v-btn>
                            </template>
                            <span>Cancel (Esc)</span>
                          </v-tooltip>
                        </div>
                      </div>
                    </div>
                  </template>

                  <!-- Empty -->
                  <template v-slot:no-data>
                    <div class="text-center py-8 grey--text">
                      <v-icon large color="grey lighten-2">mdi-package-variant-closed-remove</v-icon>
                      <div class="mt-2" style="font-size: 13px;">No catalog data in this period</div>
                    </div>
                  </template>
                </v-data-table>
              </div>

              <!-- Pagination -->
              <div v-if="catalog.totalPage > 1" class="d-flex justify-end mt-3">
                <v-pagination
                  v-model="catalog.page"
                  :length="catalog.totalPage"
                  :total-visible="$vuetify.breakpoint.xs ? 3 : 5"
                  color="primary"
                  circle
                  @input="fetchCatalog()"
                />
              </div>
            </v-card>

            <!-- ===== Monthly catalog table (same columns as Daily, minus Note) ===== -->
            <v-card
              rounded="lg"
              class="pa-4 catalog-card catalog-card--month mb-4"
              :class="{ capturing: capture.isCapturing }"
              elevation="1"
            >
              <div class="d-flex align-center justify-space-between mb-4" style="gap: 12px; flex-wrap: wrap;">
                <h3 class="font-weight-bold mb-0 d-flex align-center" style="font-size: 15px; color: #5e35b1;">
                  <v-icon color="deep-purple" small class="mr-1">mdi-calendar-month</v-icon>
                  Monthly Catalog ({{ catalog.platformTab === 1 ? 'Shopify' : 'Dropnoxe' }})
                  <span class="catalog-card__period">{{ monthLabel(selectedDate) }}</span>
                  <v-chip
                    v-if="activeMonthlyList.length"
                    x-small
                    label
                    color="deep-purple"
                    outlined
                    class="ml-2 font-weight-bold"
                  >
                    {{ activeMonthlyList.length }}
                  </v-chip>
                </h3>
                <div class="d-flex align-center" style="gap: 6px; flex-wrap: wrap;">
                  <v-btn
                    depressed
                    small
                    color="deep-purple"
                    dark
                    :loading="catalog.isLoading"
                    :disabled="capture.isCapturing"
                    class="no-capture"
                    @click="fetchCatalog(true)"
                  >
                    <v-icon small>mdi-refresh</v-icon>
                  </v-btn>
                </div>
              </div>

              <!-- Table -->
              <div class="table-scroll-wrapper">
                <v-data-table
                  :headers="catalogMonthlyHeaders"
                  :items="monthlyPagedList"
                  :loading="catalog.isLoading"
                  :items-per-page="-1"
                  hide-default-footer
                  item-key="catalog"
                  class="report-table report-table--month"
                >
                  <!-- Image -->
                  <template v-slot:[`item.image`]="{ item }">
                    <div class="catalog-thumb-wrap">
                      <v-img
                        v-if="getCatalogImage(item)"
                        :src="imageHelpers.url(getCatalogImage(item), { width: 80, height: 80 })"
                        width="48"
                        height="48"
                        class="rounded catalog-thumb"
                        contain
                      />
                      <div v-else class="catalog-thumb-placeholder">
                        <v-icon color="grey lighten-1">mdi-image-off-outline</v-icon>
                      </div>
                    </div>
                  </template>

                  <!-- Name -->
                  <template v-slot:[`item.name`]="{ item }">
                    <v-tooltip top max-width="320">
                      <template v-slot:activator="{ on, attrs }">
                        <div v-bind="attrs" v-on="on" class="cell-clamp font-weight-medium primary--text">
                          {{ getCatalogName(item) }}
                        </div>
                      </template>
                      <span>{{ getCatalogName(item) }}</span>
                    </v-tooltip>
                  </template>

                  <!-- Spent -->
                  <template v-slot:[`item.totalSpent`]="{ item }">
                    <span class="red--text text--darken-1 font-weight-medium">{{
                      formatCurrency(item.totalSpent)
                    }}</span>
                  </template>

                  <!-- Revenue -->
                  <template v-slot:[`item.totalRev`]="{ item }">
                    <span class="green--text text--darken-2 font-weight-medium">{{
                      formatCurrency(item.totalRev)
                    }}</span>
                  </template>

                  <!-- ROAS -->
                  <template v-slot:[`item.roas`]="{ item }">
                    <span class="font-weight-medium" :class="roasTextClass(item.roas)">
                      {{ Number(item.roas || 0).toFixed(2) }}x
                    </span>
                  </template>

                  <!-- Order -->
                  <template v-slot:[`item.totalOrder`]="{ item }">
                    <div class="order-cell">
                      <span class="order-cell__num">{{ (item.totalOrder || 0).toLocaleString() }}</span>
                    </div>
                  </template>

                  <!-- Empty -->
                  <template v-slot:no-data>
                    <div class="text-center py-8 grey--text">
                      <v-icon large color="grey lighten-2">mdi-package-variant-closed-remove</v-icon>
                      <div class="mt-2" style="font-size: 13px;">No catalog data this month</div>
                    </div>
                  </template>
                </v-data-table>
              </div>

              <!-- Pagination (client-side; hidden during capture so all rows print) -->
              <div v-if="monthlyTotalPage > 1 && !capture.isCapturing" class="d-flex justify-end mt-3 no-capture">
                <v-pagination
                  v-model="catalog.monthlyPage"
                  :length="monthlyTotalPage"
                  :total-visible="$vuetify.breakpoint.xs ? 3 : 5"
                  color="deep-purple"
                  circle
                />
              </div>
            </v-card>

            <v-row no-gutters class="mt-4" style="gap: 0;">
              <v-col cols="12">
                <v-card rounded="lg" class="pa-4 plan-card" :class="{ 'plan-card--dirty': isPlanDirty }" elevation="1">
                  <!-- Plan header -->
                  <div class="d-flex align-center justify-space-between mb-3" style="gap: 8px; flex-wrap: wrap;">
                    <div class="d-flex align-center" style="gap: 8px;">
                      <div class="plan-card__icon">
                        <v-icon color="white">mdi-calendar-arrow-right</v-icon>
                      </div>
                      <div>
                        <div class="plan-card__title">Daily Plan</div>
                        <div class="plan-card__subtitle">
                          {{ formatDisplayDate(selectedDate) }}
                          <template v-if="!capture.isCapturing">
                            <span v-if="isPlanDirty" class="plan-card__dirty-tag">• Unsaved changes</span>
                            <span v-else-if="planText" class="plan-card__saved-tag">• Saved</span>
                          </template>
                        </div>
                      </div>
                    </div>
                    <div v-if="!capture.isCapturing" class="d-flex align-center no-capture" style="gap: 6px;">
                      <span class="plan-card__counter" :class="{ 'plan-card__counter--warn': planText.length > 1800 }">
                        {{ planText.length }} / 2000
                      </span>
                      <v-btn
                        small
                        text
                        color="grey darken-1"
                        style="text-transform: none;"
                        :disabled="!isPlanDirty || savingPlan"
                        @click="resetPlan"
                      >
                        <v-icon small left>mdi-undo</v-icon>
                        Reset
                      </v-btn>
                      <v-btn
                        small
                        depressed
                        color="primary"
                        :loading="savingPlan"
                        :disabled="!planText.trim() || !isPlanDirty"
                        @click="savePlan"
                      >
                        <v-icon small left>mdi-content-save-outline</v-icon>
                        Save Plan
                      </v-btn>
                    </div>
                  </div>

                  <!-- Plan content: textarea normally, plain div during capture -->
                  <div v-if="capture.isCapturing" class="plan-display">
                    <span v-if="planText">{{ planText }}</span>
                    <span v-else class="plan-display__placeholder">— no plan —</span>
                  </div>
                  <v-textarea
                    v-else
                    v-model="planText"
                    placeholder="Tomorrow's revenue target, campaigns to scale, budgets, ideas to test…"
                    outlined
                    rows="4"
                    auto-grow
                    hide-details
                    no-resize
                    color="primary"
                    maxlength="2000"
                    class="plan-textarea"
                  />

                  <!-- Saved alert (flash) -->
                  <v-slide-y-transition>
                    <div v-if="planSaved" class="plan-card__saved-flash mt-3">
                      <v-icon size="18" color="success" class="mr-1">mdi-check-circle</v-icon>
                      Plan saved successfully
                    </div>
                  </v-slide-y-transition>
                </v-card>
              </v-col>
            </v-row>
          </div>
        </v-col>

        <!-- ===== RIGHT: Unmapped Campaigns ===== -->
        <v-col cols="12" lg="6">
          <v-card rounded="lg" class="pa-4" elevation="1">
            <div class="d-flex align-center justify-space-between mb-4" style="gap: 12px; flex-wrap: wrap;">
              <h3 class="font-weight-bold mb-0 d-flex align-center" style="font-size: 15px; color: #e65100;">
                <v-icon color="orange darken-2" small class="mr-1">mdi-link-variant-off</v-icon>
                Unmapped Campaigns
                <v-chip
                  v-if="unmapped.total"
                  x-small
                  label
                  color="orange darken-2"
                  outlined
                  class="ml-2 font-weight-bold"
                >
                  {{ unmapped.total }}
                </v-chip>
              </h3>
              <v-btn
                depressed
                small
                color="orange darken-2"
                dark
                :loading="unmapped.isLoading"
                @click="fetchUnmapped(true)"
              >
                <v-icon small>mdi-refresh</v-icon>
              </v-btn>
            </div>

            <div class="info-banner mb-3">
              <v-icon small color="orange darken-2" class="mr-1">mdi-information-outline</v-icon>
              <span>Click <strong>Edit</strong> to map a campaign with a product.</span>
            </div>

            <div class="table-scroll-wrapper unmapped-table-wrap">
              <v-data-table
                :headers="unmappedHeaders"
                :items="unmapped.list"
                :loading="unmapped.isLoading"
                :items-per-page="-1"
                hide-default-footer
                item-key="_id"
                class="report-table unmapped-table"
              >
                <!-- Account block -->
                <template v-slot:[`item.account`]="{ item }">
                  <div class="account-block">
                    <div class="account-block__name">
                      <v-icon size="14" color="primary" class="mr-1">mdi-account-circle</v-icon>
                      {{ item.accountName || '—' }}
                    </div>
                    <div class="account-block__id" @click="copyText(item.accountId)">
                      <span class="kbd-chip">{{ item.accountId }}</span>
                      <v-icon x-small class="ml-1 grey--text">mdi-content-copy</v-icon>
                    </div>
                    <div class="account-block__person">
                      {{ [item.firstName, item.lastName].filter(Boolean).join(' ') || '—' }}
                    </div>
                    <div v-if="item.email" class="account-block__email">
                      <v-icon size="12" color="grey">mdi-email-outline</v-icon>
                      {{ item.email }}
                    </div>
                  </div>
                </template>

                <!-- Ads block -->
                <template v-slot:[`item.ads`]="{ item }">
                  <div class="ads-block">
                    <div class="ads-block__media">
                      <a
                        v-if="item.link"
                        :href="item.link"
                        target="_blank"
                        rel="noopener noreferrer"
                        class="ads-block__thumb-wrap"
                      >
                        <v-img
                          v-if="item.thumbnailUrl"
                          :src="item.thumbnailUrl"
                          width="56"
                          height="56"
                          class="ads-block__thumb"
                          cover
                        />
                        <div v-else class="ads-block__thumb-placeholder">
                          <v-icon color="grey lighten-1">mdi-image-off-outline</v-icon>
                        </div>
                        <v-icon size="14" color="white" class="ads-block__link-icon">mdi-open-in-new</v-icon>
                      </a>
                      <div v-else class="ads-block__thumb-wrap">
                        <v-img
                          v-if="item.thumbnailUrl"
                          :src="item.thumbnailUrl"
                          width="56"
                          height="56"
                          class="ads-block__thumb"
                          cover
                        />
                        <div v-else class="ads-block__thumb-placeholder">
                          <v-icon color="grey lighten-1">mdi-image-off-outline</v-icon>
                        </div>
                      </div>
                    </div>
                    <div class="ads-block__info">
                      <v-tooltip top max-width="380">
                        <template v-slot:activator="{ on, attrs }">
                          <div v-bind="attrs" v-on="on" class="ads-block__campaign">
                            {{ item.campaign || '—' }}
                          </div>
                        </template>
                        <span>{{ item.campaign || '—' }}</span>
                      </v-tooltip>
                      <v-tooltip top max-width="380">
                        <template v-slot:activator="{ on, attrs }">
                          <div v-bind="attrs" v-on="on" class="ads-block__adname">
                            <v-icon size="12" color="grey">mdi-bullhorn-outline</v-icon>
                            {{ item.adName || '—' }}
                          </div>
                        </template>
                        <span>{{ item.adName || '—' }}</span>
                      </v-tooltip>
                      <v-tooltip v-if="item.adContent && item.adContent !== item.campaign" top max-width="380">
                        <template v-slot:activator="{ on, attrs }">
                          <div v-bind="attrs" v-on="on" class="ads-block__adcontent">
                            <v-icon size="12" color="grey">mdi-text</v-icon>
                            {{ item.adContent }}
                          </div>
                        </template>
                        <span>{{ item.adContent }}</span>
                      </v-tooltip>
                      <div class="ads-block__meta">
                        <v-tooltip top max-width="380">
                          <template v-slot:activator="{ on, attrs }">
                            <span
                              v-bind="attrs"
                              v-on="on"
                              class="kbd-chip kbd-chip--truncate"
                              @click="copyText(item.campaignId)"
                            >
                              <v-icon size="11" class="grey--text mr-1">mdi-identifier</v-icon>
                              <span class="kbd-chip__label">Campaign ID:</span>
                              <span class="kbd-chip__value">{{ item.campaignId }}</span>
                              <v-icon size="11" class="ml-1 grey--text">mdi-content-copy</v-icon>
                            </span>
                          </template>
                          <span>Campaign ID: {{ item.campaignId }}</span>
                        </v-tooltip>
                        <v-tooltip v-if="item.postId || item.post_id" top max-width="380">
                          <template v-slot:activator="{ on, attrs }">
                            <span
                              v-bind="attrs"
                              v-on="on"
                              class="kbd-chip kbd-chip--truncate"
                              @click="copyText(item.postId || item.post_id)"
                            >
                              <v-icon size="11" class="grey--text mr-1">mdi-post-outline</v-icon>
                              <span class="kbd-chip__label">Post ID:</span>
                              <span class="kbd-chip__value">{{ item.postId || item.post_id }}</span>
                              <v-icon size="11" class="ml-1 grey--text">mdi-content-copy</v-icon>
                            </span>
                          </template>
                          <span>Post ID: {{ item.postId || item.post_id }}</span>
                        </v-tooltip>
                        <span v-if="item.date" class="date-chip">
                          <v-icon size="11" color="grey">mdi-calendar</v-icon>
                          {{ item.date }}
                        </span>
                      </div>
                    </div>
                  </div>
                </template>

                <!-- Stats -->
                <template v-slot:[`item.stats`]="{ item }">
                  <div class="stats-block">
                    <div class="stat-pill stat-pill--clicks">
                      <v-icon size="14" color="blue darken-2" class="mr-1">mdi-cursor-default-click-outline</v-icon>
                      <span class="stat-pill__label">Clicks</span>
                      <span class="stat-pill__value">{{ Number(item.totalClicks || 0).toLocaleString() }}</span>
                    </div>
                    <div class="stat-pill stat-pill--spend">
                      <v-icon size="14" color="red darken-2" class="mr-1">mdi-cash-multiple</v-icon>
                      <span class="stat-pill__label">Spend</span>
                      <span class="stat-pill__value">{{ formatCurrency(item.totalSpend) }}</span>
                    </div>
                  </div>
                </template>

                <!-- Edit action -->
                <template v-slot:[`item.actions`]="{ item }">
                  <v-btn small depressed color="primary" style="text-transform: none;" @click="openMapDialog(item)">
                    <v-icon small left>mdi-link-variant-plus</v-icon>
                    Edit
                  </v-btn>
                </template>

                <template v-slot:no-data>
                  <div class="text-center py-8 grey--text">
                    <v-icon large color="grey lighten-2">mdi-check-circle-outline</v-icon>
                    <div class="mt-2" style="font-size: 13px;">All campaigns are mapped</div>
                  </div>
                </template>
              </v-data-table>
            </div>

            <!-- Pagination -->
            <div v-if="unmapped.totalPage > 1" class="d-flex justify-end mt-3">
              <v-pagination
                v-model="unmapped.page"
                :length="unmapped.totalPage"
                :total-visible="$vuetify.breakpoint.xs ? 3 : 5"
                color="orange darken-2"
                circle
                @input="fetchUnmapped()"
              />
            </div>
          </v-card>
        </v-col>
      </v-row>
    </div>

    <!-- ===== Capture overlay (covers UI while DOM is rendered off-screen) ===== -->
    <transition name="capture-fade">
      <div v-if="capture.isCapturing" class="capture-overlay">
        <div class="capture-overlay__card">
          <div class="capture-overlay__spinner">
            <v-progress-circular indeterminate color="primary" size="52" width="4" />
            <v-icon class="capture-overlay__camera" color="primary" size="22">mdi-camera</v-icon>
          </div>
          <div class="capture-overlay__title">Generating report image…</div>
          <div class="capture-overlay__subtitle">Capturing full catalog data, please wait a moment.</div>
          <div class="capture-overlay__progress">
            <div class="capture-overlay__progress-bar"></div>
          </div>
        </div>
      </div>
    </transition>

    <!-- ===== Map Product Dialog ===== -->
    <v-dialog v-model="mapDialog.show" max-width="720" persistent scrollable>
      <v-card class="map-dialog-card">
        <v-card-title class="d-flex align-center" style="gap: 8px;">
          <v-icon color="primary">mdi-link-variant-plus</v-icon>
          <span class="font-weight-bold primary--text" style="font-size: 18px;">Map Product to Campaign</span>
          <v-spacer />
          <v-btn icon @click="closeMapDialog">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <!-- Campaign summary -->
        <div class="px-6">
          <div class="campaign-summary">
            <div class="campaign-summary__row">
              <span class="label">Campaign ID</span>
              <span class="value">{{ mapDialog.campaign && mapDialog.campaign.campaignId }}</span>
            </div>
            <div class="campaign-summary__row">
              <span class="label">Campaign Name</span>
              <span class="value">{{ (mapDialog.campaign && mapDialog.campaign.campaign) || '—' }}</span>
            </div>
            <div class="campaign-summary__row">
              <span class="label">Ad Name</span>
              <span class="value">{{ (mapDialog.campaign && mapDialog.campaign.adName) || '—' }}</span>
            </div>
            <div class="campaign-summary__row">
              <span class="label">Account</span>
              <span class="value">{{ (mapDialog.campaign && mapDialog.campaign.accountName) || '—' }}</span>
            </div>
            <div class="campaign-summary__row">
              <span class="label">Total Spend</span>
              <span class="value red--text text--darken-1 font-weight-bold">
                {{ formatCurrency(mapDialog.campaign && mapDialog.campaign.totalSpend) }}
              </span>
            </div>
          </div>
        </div>

        <!-- Platform Tabs -->
        <div class="px-6 mb-2">
          <v-tabs
            v-model="mapDialog.activeTab"
            color="primary"
            height="36"
            active-class="font-weight-bold"
            @change="onTabChange"
          >
            <v-tab class="text-capitalize">
              <v-icon small left>mdi-storefront-outline</v-icon>
              Dropnoxe
            </v-tab>
            <v-tab class="text-capitalize">
              <v-icon small left>mdi-shopify</v-icon>
              Shopify
            </v-tab>
          </v-tabs>
        </div>

        <v-divider class="mb-3" />

        <!-- Product search -->
        <div class="px-6">
          <v-text-field
            v-model="productSearch.text"
            placeholder="Search product by name..."
            prepend-inner-icon="mdi-magnify"
            hide-details
            outlined
            dense
            clearable
            @input="onProductSearchInput"
            @click:clear="onProductSearchInput('')"
          />
        </div>

        <!-- Product list -->
        <v-card-text class="map-dialog-list pa-0 mt-3">
          <div v-if="productSearch.isLoading && !productSearch.list.length" class="py-8 text-center">
            <v-progress-circular indeterminate color="primary" size="32" />
            <div class="mt-2 grey--text">Loading products…</div>
          </div>

          <div v-else-if="!productSearch.isLoading && !productSearch.list.length" class="py-8 text-center grey--text">
            <v-icon large color="grey lighten-2">mdi-magnify-close</v-icon>
            <div class="mt-2" style="font-size: 13px;">No products found</div>
          </div>

          <v-list v-else class="py-0">
            <v-list-item
              v-for="product in productSearch.list"
              :key="product._id || product.id"
              class="product-row"
              :class="{ 'product-row--selected': isSelectedProduct(product) }"
              @click="selectProduct(product)"
            >
              <div class="product-thumb-wrap">
                <v-img
                  v-if="getProductImage(product)"
                  :src="imageHelpers.url(getProductImage(product), { width: 80, height: 80 })"
                  width="48"
                  height="48"
                  class="rounded product-thumb"
                  contain
                />
                <div v-else class="product-thumb-placeholder">
                  <v-icon color="grey lighten-1">mdi-image-off-outline</v-icon>
                </div>
              </div>
              <v-list-item-content class="ml-3">
                <v-list-item-title class="font-weight-medium">{{ getProductName(product) }}</v-list-item-title>
                <v-list-item-subtitle class="grey--text">
                  <span>ID: {{ product._id || product.id || '—' }}</span>
                  <span v-if="product.shopifyProductId" class="ml-2">
                    | Shopify ID: {{ product.shopifyProductId }}
                  </span>
                </v-list-item-subtitle>
              </v-list-item-content>
              <v-list-item-action>
                <v-icon v-if="isSelectedProduct(product)" color="primary" size="24">mdi-check-circle</v-icon>
                <v-icon v-else color="grey lighten-1" size="24">mdi-circle-outline</v-icon>
              </v-list-item-action>
            </v-list-item>
          </v-list>

          <!-- Pagination -->
          <div v-if="productSearch.totalPage > 1" class="d-flex justify-center py-3">
            <v-pagination
              v-model="productSearch.page"
              :length="productSearch.totalPage"
              :total-visible="$vuetify.breakpoint.xs ? 3 : 5"
              color="primary"
              circle
              @input="fetchProducts()"
            />
          </div>
        </v-card-text>

        <v-divider />

        <v-card-actions class="px-6 py-3">
          <div v-if="mapDialog.selectedProduct" class="selected-product-pill">
            <v-icon small color="primary">mdi-check-circle</v-icon>
            <span class="ml-1">{{ getProductName(mapDialog.selectedProduct) }}</span>
          </div>
          <v-spacer />
          <v-btn text @click="closeMapDialog">Cancel</v-btn>
          <v-btn
            color="primary"
            depressed
            :disabled="!mapDialog.selectedProduct"
            :loading="mapDialog.saving"
            @click="saveMap"
          >
            <v-icon small left>mdi-content-save</v-icon>
            Save Mapping
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { reportApi } from '@/apis/report';
import imageHelpers from '@/helpers/image';

export default {
  name: 'ReportCatalogPage',
  data() {
    const today = new Date().toISOString().slice(0, 10);
    return {
      today,
      imageHelpers,

      // Date filter (single date)
      selectedDate: today,
      dateMenu: false,

      // Account of current viewer (carries role, e.g. SALE_MANAGER)
      account: null,

      // Catalog list
      catalog: {
        isLoading: false,
        list: [],
        listDropnoxe: [],
        listShopify: [],
        page: 1,
        limit: 50,
        total: 0,
        totalPage: 1,
        // Monthly catalog (full month aggregation, paginated client-side)
        monthlyList: [],
        monthlyListDropnoxe: [],
        monthlyListShopify: [],
        monthlyPage: 1,
        monthlyLimit: 10,
        // Single Master Platform tab: 0 = Dropnoxe, 1 = Shopify
        platformTab: 0,
        summary: {
          totalSpent: 0,
          totalRev: 0,
          totalOrder: 0,
          roas: 0,
          profitSeller: 0,
        },
        summaryAll: { totalSpent: 0, totalRev: 0, totalOrder: 0, roas: 0, profitSeller: 0 },
        summaryDropnoxe: { totalSpent: 0, totalRev: 0, totalOrder: 0, roas: 0, profitSeller: 0 },
        summaryShopify: { totalSpent: 0, totalRev: 0, totalOrder: 0, roas: 0, profitSeller: 0 },
        monthlySummary: {
          totalSpent: 0,
          totalRev: 0,
          totalOrder: 0,
          roas: 0,
          profitSeller: 0,
        },
        monthlySummaryAll: { totalSpent: 0, totalRev: 0, totalOrder: 0, roas: 0, profitSeller: 0 },
        monthlySummaryDropnoxe: { totalSpent: 0, totalRev: 0, totalOrder: 0, roas: 0, profitSeller: 0 },
        monthlySummaryShopify: { totalSpent: 0, totalRev: 0, totalOrder: 0, roas: 0, profitSeller: 0 },
      },
      catalogHeaders: [
        { text: 'Image', value: 'image', sortable: false, width: '80px' },
        { text: 'Catalog Name', value: 'name', sortable: false },
        { text: 'Orders', value: 'totalOrder', sortable: true, width: '110px', align: 'center' },
        { text: 'Spent', value: 'totalSpent', sortable: true, width: '120px', align: 'end' },
        { text: 'Revenue', value: 'totalRev', sortable: true, width: '120px', align: 'end' },
        { text: 'ROAS', value: 'roas', sortable: true, width: '90px', align: 'end' },
        { text: 'Note', value: 'note', sortable: false, width: '280px' },
      ],

      // Unmapped campaigns
      unmapped: {
        isLoading: false,
        list: [],
        page: 1,
        limit: 20,
        total: 0,
        totalPage: 1,
      },
      unmappedHeaders: [
        { text: 'Account', value: 'account', sortable: false, width: '240px' },
        { text: 'Ads', value: 'ads', sortable: false },
        { text: 'Stats', value: 'stats', sortable: false, width: '200px' },
        { text: '', value: 'actions', sortable: false, width: '110px', align: 'center' },
      ],

      // Map product dialog
      mapDialog: {
        show: false,
        campaign: null,
        selectedProduct: null,
        saving: false,
        activeTab: 0,
      },

      // Product search inside dialog
      productSearch: {
        text: '',
        list: [],
        page: 1,
        limit: 20,
        total: 0,
        totalPage: 1,
        isLoading: false,
      },
      // eslint-disable-next-line vue/no-reserved-keys
      _searchTimer: null,

      // Capture (export catalog as image)
      capture: {
        isCapturing: false,
        savedPage: 1,
        savedLimit: 50,
        savedList: [],
      },
      captureTimestamp: '',

      // Plan
      planText: '',
      planLastSaved: '',
      savingPlan: false,
      planSaved: false,
    };
  },
  computed: {
    isLoadingAll() {
      return this.catalog.isLoading || this.unmapped.isLoading;
    },
    isSaleManager() {
      return !!(this.account && this.account.role === 'SALE_MANAGER');
    },
    isPlanDirty() {
      return (this.planText || '').trim() !== (this.planLastSaved || '').trim();
    },
    currentUser() {
      try {
        const raw = localStorage.getItem('dropnoxe-user');
        if (!raw) return null;
        return JSON.parse(raw);
      } catch (e) {
        return null;
      }
    },
    captureUserName() {
      const u = this.currentUser;
      if (!u) return 'Unknown user';
      const full = [u.firstName, u.lastName]
        .filter(Boolean)
        .join(' ')
        .trim();
      return full || u.name || u.fullName || u.displayName || u.email || 'Unknown user';
    },
    captureUserEmail() {
      const u = this.currentUser;
      return (u && u.email) || '—';
    },
    // Monthly table shares the daily columns, minus the Note column
    catalogMonthlyHeaders() {
      return this.catalogHeaders.filter(h => h.value !== 'note');
    },
    activeDailySummary() {
      if (this.catalog.platformTab === 1) return this.catalog.summaryShopify || {};
      return this.catalog.summaryDropnoxe || {};
    },
    activeMonthlySummary() {
      if (this.catalog.platformTab === 1) return this.catalog.monthlySummaryShopify || {};
      return this.catalog.monthlySummaryDropnoxe || {};
    },
    activeDailyList() {
      if (this.catalog.platformTab === 1) return this.catalog.listShopify || [];
      return this.catalog.listDropnoxe || [];
    },
    activeMonthlyList() {
      if (this.catalog.platformTab === 1) return this.catalog.monthlyListShopify || [];
      return this.catalog.monthlyListDropnoxe || [];
    },
    monthlyTotalPage() {
      const len = this.activeMonthlyList.length || 0;
      return Math.max(1, Math.ceil(len / this.catalog.monthlyLimit));
    },
    monthlyPagedList() {
      if (this.capture.isCapturing) return this.activeMonthlyList;
      const start = (this.catalog.monthlyPage - 1) * this.catalog.monthlyLimit;
      return this.activeMonthlyList.slice(start, start + this.catalog.monthlyLimit);
    },
  },
  created() {
    this.refreshAll();
  },
  methods: {
    /* ---------- Formatting helpers ---------- */
    formatCurrency(val) {
      if (val === null || val === undefined || val === '') return '$0.00';
      const num = parseFloat(val);
      if (isNaN(num)) return '$0.00';
      return '$' + num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    formatDisplayDate(dateStr) {
      if (!dateStr) return '';
      const d = new Date(dateStr + 'T00:00:00');
      return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
    },
    toDateString(d) {
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${y}-${m}-${day}`;
    },
    /* ---------- Date nav ---------- */
    goPrevDay() {
      const d = new Date(this.selectedDate + 'T00:00:00');
      d.setDate(d.getDate() - 1);
      this.selectedDate = this.toDateString(d);
      this.refreshAll();
    },
    goNextDay() {
      const d = new Date(this.selectedDate + 'T00:00:00');
      d.setDate(d.getDate() + 1);
      const next = this.toDateString(d);
      if (next > this.today) return;
      this.selectedDate = next;
      this.refreshAll();
    },
    goToday() {
      if (this.selectedDate === this.today) return;
      this.selectedDate = this.today;
      this.refreshAll();
    },
    monthLabel(dateStr) {
      if (!dateStr) return '';
      const d = new Date(dateStr + 'T00:00:00');
      return d.toLocaleString('en-US', { month: 'long', year: 'numeric' });
    },

    /* ---------- Data helpers ---------- */
    getCatalogImage(item) {
      if (!item) return '';
      const detail = item.catalogDetail || {};
      return (
        (detail.images && detail.images[0]) ||
        detail.image ||
        detail.thumbnail ||
        item.image ||
        (item.images && item.images[0]) ||
        ''
      );
    },
    getCatalogName(item) {
      if (!item) return '—';
      const detail = item.catalogDetail || {};
      return detail.title || detail.name || item.title || item.name || '—';
    },
    roasTextClass(roas) {
      const r = Number(roas) || 0;
      if (r >= 2) return 'green--text text--darken-2';
      if (r >= 1) return 'orange--text text--darken-2';
      return 'red--text text--darken-1';
    },
    roasChipClass(roas) {
      const r = Number(roas) || 0;
      if (r >= 2) return 'totals-chip--roas-good';
      if (r >= 1) return 'totals-chip--roas-ok';
      return 'totals-chip--roas-bad';
    },
    getProductImage(p) {
      if (!p) return '';
      if (typeof p.image === 'string') return p.image;
      if (p.image && p.image.src) return p.image.src;
      if (Array.isArray(p.images) && p.images.length) {
        const first = p.images[0];
        return typeof first === 'string' ? first : first?.src || '';
      }
      return (p.imageSrcs && p.imageSrcs[0]) || p.thumbnail || p.image_url || '';
    },
    getProductName(p) {
      if (!p) return '—';
      return p.title || p.name || p.product_name || '—';
    },
    isSelectedProduct(p) {
      if (!this.mapDialog.selectedProduct) return false;
      const a = p._id || p.id;
      const b = this.mapDialog.selectedProduct._id || this.mapDialog.selectedProduct.id;
      return a && b && a === b;
    },

    /* ---------- Fetches ---------- */
    async refreshAll() {
      await Promise.all([this.fetchCatalog(true), this.fetchUnmapped()]);
    },

    async fetchCatalog(reset = false) {
      if (reset) this.catalog.page = 1;
      this.catalog.isLoading = true;
      try {
        const { data } = await reportApi.getReportCatalogBySeller({
          page: String(this.catalog.page),
          limit: String(this.catalog.limit),
          date: this.selectedDate,
        });
        const listDropnoxe = Array.isArray(data?.data) ? data.data : Array.isArray(data) ? data : [];
        const listShopify = Array.isArray(data?.dataShopify) ? data.dataShopify : [];
        const monthlyListDropnoxe = Array.isArray(data?.monthlyData) ? data.monthlyData : [];
        const monthlyListShopify = Array.isArray(data?.monthlyDataShopify) ? data.monthlyDataShopify : [];

        const pagination = data?.pagination || {};
        const summary = data?.summary || {};
        const summaryShopify = data?.summaryShopify || {};
        const summaryAll = data?.summaryAll || summary;

        const monthlySummary = data?.monthlySummary || {};
        const monthlySummaryShopify = data?.monthlySummaryShopify || {};
        const monthlySummaryAll = data?.monthlySummaryAll || monthlySummary;

        this.account = data?.account || null;
        [...listDropnoxe, ...listShopify, ...monthlyListDropnoxe, ...monthlyListShopify].forEach(it => {
          it.note = it.note || '';
          it._noteLastSaved = it.note;
        });

        this.catalog.listDropnoxe = listDropnoxe;
        this.catalog.listShopify = listShopify;
        this.catalog.list = listDropnoxe;

        this.catalog.monthlyListDropnoxe = monthlyListDropnoxe;
        this.catalog.monthlyListShopify = monthlyListShopify;
        this.catalog.monthlyList = monthlyListDropnoxe;

        this.catalog.total = pagination.total ?? listDropnoxe.length + listShopify.length;
        this.catalog.totalPage =
          pagination.totalPage ?? Math.max(1, Math.ceil(this.catalog.total / this.catalog.limit));
        if (this.catalog.monthlyPage > this.monthlyTotalPage) this.catalog.monthlyPage = 1;

        const parseSummary = s => ({
          totalSpent: s.totalSpent || 0,
          totalRev: s.totalRev || 0,
          totalOrder: s.totalOrder || 0,
          roas: s.roas || 0,
          profitSeller: s.profitSeller || 0,
        });

        this.catalog.summaryAll = parseSummary(summaryAll);
        this.catalog.summaryDropnoxe = parseSummary(summary);
        this.catalog.summaryShopify = parseSummary(summaryShopify);
        this.catalog.summary = this.catalog.summaryAll;

        this.catalog.monthlySummaryAll = parseSummary(monthlySummaryAll);
        this.catalog.monthlySummaryDropnoxe = parseSummary(monthlySummary);
        this.catalog.monthlySummaryShopify = parseSummary(monthlySummaryShopify);
        this.catalog.monthlySummary = this.catalog.monthlySummaryAll;

        this.planText = data?.plan || '';
        this.planLastSaved = this.planText;
      } catch (e) {
        console.error('fetchCatalog', e);
        this.catalog.list = [];
        this.catalog.listDropnoxe = [];
        this.catalog.listShopify = [];
        this.catalog.total = 0;
        this.catalog.totalPage = 1;
        this.catalog.monthlyList = [];
        this.catalog.monthlyListDropnoxe = [];
        this.catalog.monthlyListShopify = [];
        this.catalog.monthlyPage = 1;
        const emptySum = { totalSpent: 0, totalRev: 0, totalOrder: 0, roas: 0, profitSeller: 0 };
        this.catalog.summary = { ...emptySum };
        this.catalog.summaryAll = { ...emptySum };
        this.catalog.summaryDropnoxe = { ...emptySum };
        this.catalog.summaryShopify = { ...emptySum };
        this.catalog.monthlySummary = { ...emptySum };
        this.catalog.monthlySummaryAll = { ...emptySum };
        this.catalog.monthlySummaryDropnoxe = { ...emptySum };
        this.catalog.monthlySummaryShopify = { ...emptySum };
        this.$store.commit('setMessages', {
          messages: e.response?.data?.message || 'Failed to load catalog report',
          type: 'error',
        });
      } finally {
        this.catalog.isLoading = false;
      }
    },

    async fetchUnmapped(reset = false) {
      if (reset) this.unmapped.page = 1;
      this.unmapped.isLoading = true;
      try {
        const { data } = await reportApi.getReportCatalogListNotMap({
          page: String(this.unmapped.page),
          limit: String(this.unmapped.limit),
        });
        const list = Array.isArray(data?.data) ? data.data : Array.isArray(data) ? data : [];
        this.unmapped.list = list;
        this.unmapped.total = (data && (data.total ?? data?.pagination?.total)) || list.length;
        this.unmapped.totalPage =
          (data && (data.totalPage ?? data?.pagination?.totalPage)) ||
          Math.max(1, Math.ceil(this.unmapped.total / this.unmapped.limit));
      } catch (e) {
        console.error('fetchUnmapped', e);
        this.unmapped.list = [];
        this.unmapped.total = 0;
        this.unmapped.totalPage = 1;
        this.$store.commit('setMessages', {
          messages: e.response?.data?.message || 'Failed to load unmapped campaigns',
          type: 'error',
        });
      } finally {
        this.unmapped.isLoading = false;
      }
    },

    async fetchProducts() {
      this.productSearch.isLoading = true;
      try {
        const apiMethod =
          this.mapDialog.activeTab === 1
            ? reportApi.getReportCatalogListShopifyProduct
            : reportApi.getReportCatalogListProduct;
        const { data } = await apiMethod({
          page: String(this.productSearch.page),
          limit: String(this.productSearch.limit),
          product_name: this.productSearch.text || '',
        });
        const list = Array.isArray(data?.data) ? data.data : Array.isArray(data) ? data : [];
        const pagination = data?.pagination || {};
        this.productSearch.list = list;
        this.productSearch.total = pagination.total ?? list.length;
        this.productSearch.totalPage =
          pagination.totalPage ?? Math.max(1, Math.ceil(this.productSearch.total / this.productSearch.limit));
      } catch (e) {
        console.error('fetchProducts', e);
        this.productSearch.list = [];
        this.productSearch.total = 0;
        this.productSearch.totalPage = 1;
      } finally {
        this.productSearch.isLoading = false;
      }
    },

    onProductSearchInput(val) {
      this.productSearch.text = val || '';
      this.productSearch.page = 1;
      if (this._searchTimer) clearTimeout(this._searchTimer);
      this._searchTimer = setTimeout(() => {
        this.fetchProducts();
      }, 350);
    },

    onTabChange() {
      this.mapDialog.selectedProduct = null;
      this.productSearch.text = '';
      this.productSearch.page = 1;
      this.productSearch.list = [];
      this.fetchProducts();
    },

    /* ---------- Map dialog ---------- */
    openMapDialog(campaign) {
      this.mapDialog.show = true;
      this.mapDialog.campaign = campaign;
      this.mapDialog.selectedProduct = null;
      this.mapDialog.activeTab = 0;
      this.productSearch.text = '';
      this.productSearch.page = 1;
      this.productSearch.list = [];
      this.fetchProducts();
    },
    closeMapDialog() {
      this.mapDialog.show = false;
      this.mapDialog.campaign = null;
      this.mapDialog.selectedProduct = null;
      this.mapDialog.activeTab = 0;
      this.productSearch.text = '';
      this.productSearch.list = [];
      this.productSearch.page = 1;
    },
    selectProduct(p) {
      this.mapDialog.selectedProduct = p;
    },
    async saveMap() {
      if (!this.mapDialog.campaign || !this.mapDialog.selectedProduct) return;
      const c = this.mapDialog.campaign;
      // const campaignId = c.campaignId || c.campaign_id || c._id;
      const postId = c.postId || c.post_id;
      const productId = this.mapDialog.selectedProduct._id || this.mapDialog.selectedProduct.id;
      if (!postId || !productId) return;
      this.mapDialog.saving = true;
      try {
        const isShopify = this.mapDialog.activeTab === 1;
        if (isShopify) {
          await reportApi.saveMapShopifyProduct({
            post_id: postId,
            product_id: productId,
          });
        } else {
          await reportApi.saveMapProduct({
            post_id: postId,
            product_id: productId,
          });
        }
        this.$store.commit('setMessages', {
          messages: 'Campaign mapped to product successfully!',
          type: 'success',
        });
        this.closeMapDialog();
        await Promise.all([this.fetchUnmapped(true), this.fetchCatalog(true)]);
      } catch (e) {
        console.error('saveMap', e);
        this.$store.commit('setMessages', {
          messages: e.response?.data?.message || 'Failed to save mapping',
          type: 'error',
        });
      } finally {
        this.mapDialog.saving = false;
      }
    },

    /* ---------- Capture ---------- */
    loadHtml2Canvas() {
      if (window.html2canvas && window.__html2canvasProLoaded) {
        return Promise.resolve(window.html2canvas);
      }
      if (this._html2canvasPromise) return this._html2canvasPromise;
      this._html2canvasPromise = new Promise((resolve, reject) => {
        const tryLoad = (src, next) => {
          const script = document.createElement('script');
          script.src = src;
          script.onload = () => {
            if (window.html2canvas) {
              window.__html2canvasProLoaded = true;
              resolve(window.html2canvas);
            } else if (next) {
              next();
            } else {
              this._html2canvasPromise = null;
              reject(new Error('html2canvas global not found after load'));
            }
          };
          script.onerror = () => {
            if (next) {
              next();
            } else {
              this._html2canvasPromise = null;
              reject(new Error('Failed to load html2canvas-pro'));
            }
          };
          document.head.appendChild(script);
        };
        // Primary: jsDelivr; Fallback: unpkg
        tryLoad('https://cdn.jsdelivr.net/npm/html2canvas-pro@1.5.8/dist/html2canvas-pro.min.js', () =>
          tryLoad('https://unpkg.com/html2canvas-pro@1.5.8/dist/html2canvas-pro.min.js'),
        );
      });
      return this._html2canvasPromise;
    },

    async fetchAllCatalogPages() {
      const limit = 200;
      let page = 1;
      let all = [];
      let totalPage = 1;
      do {
        const { data } = await reportApi.getReportCatalogBySeller({
          page: String(page),
          limit: String(limit),
          date: this.selectedDate,
        });
        const list = Array.isArray(data?.data) ? data.data : Array.isArray(data) ? data : [];
        all = all.concat(list);
        const pg = data?.pagination || {};
        totalPage = pg.totalPage || 1;
        page += 1;
      } while (page <= totalPage);
      return all;
    },

    proxyImageUrl(url) {
      if (!url) return '';
      const stripped = String(url).replace(/^https?:\/\/cdn\.(skyfastimg|skywarex)\.com\/?/, '');
      return `https://cdn.cachebolt.com/${stripped}`;
    },

    async fetchAsDataURL(url) {
      const toDataUrl = async target => {
        const res = await fetch(target, { mode: 'cors', credentials: 'omit', cache: 'no-store' });
        if (!res.ok) throw new Error(`non-ok response: ${res.status}`);
        const blob = await res.blob();
        return await new Promise((resolve, reject) => {
          const r = new FileReader();
          r.onload = () => resolve(r.result);
          r.onerror = reject;
          r.readAsDataURL(blob);
        });
      };

      if (!url || url.startsWith('data:')) return null;

      try {
        return await toDataUrl(url);
      } catch (_) {
        try {
          return await toDataUrl(this.proxyImageUrl(url));
        } catch (_) {
          return null;
        }
      }
    },

    async inlineAllImages(rootEl) {
      const imgs = Array.from(rootEl.querySelectorAll('img'));
      const allEls = Array.from(rootEl.querySelectorAll('*'));
      const bgEls = allEls.filter(el => {
        const bg = el.style.backgroundImage || getComputedStyle(el).backgroundImage;
        return bg && bg !== 'none' && bg.indexOf('url(') === 0;
      });

      const imgTasks = imgs.map(async img => {
        if (!img.src || img.src.startsWith('data:')) return;
        const dataUrl = await this.fetchAsDataURL(img.src);
        if (dataUrl) {
          img.dataset.origSrc = img.src;
          img.src = dataUrl;
        }
      });

      const bgTasks = bgEls.map(async el => {
        const bg = el.style.backgroundImage || getComputedStyle(el).backgroundImage;
        const match = bg.match(/url\(["']?([^"')]+)["']?\)/);
        if (!match) return;
        const url = match[1];
        if (url.startsWith('data:')) return;
        const dataUrl = await this.fetchAsDataURL(url);
        if (dataUrl) {
          el.dataset.origBg = el.style.backgroundImage || '';
          el.style.backgroundImage = `url("${dataUrl}")`;
        }
      });

      await Promise.all([...imgTasks, ...bgTasks]);
    },

    restoreInlinedImages(rootEl) {
      rootEl.querySelectorAll('img[data-orig-src]').forEach(img => {
        img.src = img.dataset.origSrc;
        delete img.dataset.origSrc;
      });
      rootEl.querySelectorAll('[data-orig-bg]').forEach(el => {
        el.style.backgroundImage = el.dataset.origBg;
        delete el.dataset.origBg;
      });
    },

    async captureCatalog() {
      if (this.capture.isCapturing) return;
      this.captureTimestamp = new Date().toLocaleString('en-US', {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
      this.capture.isCapturing = true;
      this.capture.savedPage = this.catalog.page;
      this.capture.savedLimit = this.catalog.limit;
      this.capture.savedList = this.catalog.list;

      let cardEl = null;

      try {
        const html2canvasFn = await this.loadHtml2Canvas();

        let allItems = this.catalog.list;
        if (this.catalog.totalPage > 1) {
          allItems = await this.fetchAllCatalogPages();
        }
        this.catalog.list = allItems;

        await this.$nextTick();
        await new Promise(r => setTimeout(r, 300));

        cardEl = this.$refs.leftContent;
        if (!cardEl) throw new Error('Card element not found');

        // Lock to a fixed pixel width and render off-screen so the exported
        // image is identical across every browser / device (Chrome, Firefox,
        // Safari, Edge, mobile…).
        const CAPTURE_WIDTH = 1280;
        cardEl.classList.add('catalog-capture-frame');
        await this.$nextTick();
        await new Promise(r => setTimeout(r, 150));

        // Make sure icon/text fonts are fully loaded before painting
        if (document.fonts && document.fonts.ready) {
          try {
            await document.fonts.ready;
          } catch (e) {
            // ignore
          }
        }

        // Inline all images (native <img> + background-image) as base64
        // to bypass CORS / cross-origin painting issues
        await this.inlineAllImages(cardEl);
        await this.$nextTick();
        await new Promise(r => setTimeout(r, 150));

        const captureHeight = Math.max(cardEl.scrollHeight, cardEl.offsetHeight);

        const canvas = await html2canvasFn(cardEl, {
          backgroundColor: '#ffffff',
          scale: 2,
          useCORS: true,
          allowTaint: true,
          logging: false,
          width: CAPTURE_WIDTH,
          height: captureHeight,
          windowWidth: CAPTURE_WIDTH,
          windowHeight: captureHeight,
          ignoreElements: el => el.classList && el.classList.contains('no-capture'),
        });

        const link = document.createElement('a');
        const dateLabel = this.selectedDate;
        link.download = `catalog-report-${dateLabel}.png`;
        link.href = canvas.toDataURL('image/png');
        link.click();

        this.$store.commit('setMessages', {
          messages: 'Catalog report image exported successfully!',
          type: 'success',
        });
      } catch (e) {
        console.error('captureCatalog', e);
        this.$store.commit('setMessages', {
          messages: e.message || 'Failed to capture catalog report',
          type: 'error',
        });
      } finally {
        if (cardEl) {
          this.restoreInlinedImages(cardEl);
          cardEl.classList.remove('catalog-capture-frame');
        }
        if (this.capture.savedList) {
          this.catalog.list = this.capture.savedList;
        }
        this.catalog.page = this.capture.savedPage;
        this.catalog.limit = this.capture.savedLimit;
        this.capture.savedList = [];
        this.capture.isCapturing = false;
      }
    },

    /* ---------- Plan ---------- */
    resetPlan() {
      this.planText = this.planLastSaved || '';
    },
    async savePlan() {
      this.savingPlan = true;
      this.planSaved = false;
      try {
        await reportApi.saveNextPlan({
          plan: this.planText,
          date: this.selectedDate,
        });
        this.planLastSaved = this.planText;
        this.planSaved = true;
        setTimeout(() => {
          this.planSaved = false;
        }, 3000);
        await this.fetchCatalog(true);
      } catch (e) {
        console.error('savePlan', e);
        const msg = e;
        this.$store.commit('setMessages', {
          messages: Array.isArray(msg) ? msg.join(', ') : msg || 'Failed to save plan',
          type: 'error',
        });
      } finally {
        this.savingPlan = false;
      }
    },

    /* ---------- Note ---------- */
    enterEditNote(item) {
      if (!item || item._noteSaving) return;
      this.$set(item, '_noteBackup', item.note || '');
      this.$set(item, '_isEditing', true);
      this.$nextTick(() => {
        const refKey = `noteInput_${item.catalog || item._id}`;
        const ref = this.$refs[refKey];
        const inst = Array.isArray(ref) ? ref[0] : ref;
        if (inst && inst.focus) inst.focus();
      });
    },
    cancelEditNote(item) {
      if (!item) return;
      if (item._noteBackup != null) {
        this.$set(item, 'note', item._noteBackup);
      }
      this.$set(item, '_isEditing', false);
      this.$set(item, '_noteBackup', null);
    },
    async saveNoteAndExit(item) {
      if (!item) return;
      await this.saveNote(item);
      this.$set(item, '_isEditing', false);
      this.$set(item, '_noteBackup', null);
    },
    onNoteBlur(item) {
      if (!item || item._noteSaving) return;
      // Defer so a click on Save / Cancel can run its own handler first.
      // If they already left edit mode we skip; otherwise we save & exit.
      setTimeout(() => {
        if (!item._isEditing || item._noteSaving) return;
        this.saveNoteAndExit(item);
      }, 200);
    },

    async saveNote(item) {
      if (!item) return;
      const catalogId = (item.catalogDetail && item.catalogDetail._id) || item.catalog || item._id;
      if (!catalogId) return;

      const newNote = (item.note || '').trim();
      const prevNote = item._noteLastSaved == null ? '' : item._noteLastSaved;
      if (newNote === prevNote) return;

      this.$set(item, '_noteSaving', true);
      this.$set(item, '_noteSaved', false);
      try {
        await reportApi.saveNoteCatalogSeller({
          catalogId,
          date: this.selectedDate,
          note: newNote,
        });
        this.$set(item, '_noteLastSaved', newNote);
        this.$set(item, '_noteSaved', true);
        setTimeout(() => {
          this.$set(item, '_noteSaved', false);
        }, 1800);
      } catch (e) {
        console.error('saveNote', e);
        this.$store.commit('setMessages', {
          messages: e.response?.data?.message || 'Failed to save note',
          type: 'error',
        });
      } finally {
        this.$set(item, '_noteSaving', false);
      }
    },

    /* ---------- Misc ---------- */
    async copyText(text) {
      if (!text) return;
      try {
        await navigator.clipboard.writeText(String(text));
        this.$store.commit('setMessages', {
          messages: 'Copied to clipboard',
          type: 'success',
        });
      } catch (e) {
        console.error('copy', e);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.report-catalog-page {
  min-height: 100vh;
  background: #f4f6fb;
  display: flex;
  flex-direction: column;
}

.report-header {
  background: #ffffff;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
  position: sticky;
  top: 0;
  z-index: 10;
}

.report-body {
  flex: 1;
  align-items: flex-start;
}

.date-filter-card {
  background: linear-gradient(90deg, #f0f4ff 0%, #ffffff 100%);
  border: 1px solid #e3e7f5;
}

/* Totals summary */
.totals-summary {
  background: #f0f4ff;
  border-radius: 10px;
  padding: 12px 16px;
  border: 1px solid #c5cae9;

  &__values {
    display: flex;
    gap: 10px;
    flex-wrap: wrap;
  }
}

.totals-chip {
  flex: 1 1 130px;
  border-radius: 8px;
  padding: 10px 14px;
  display: flex;
  flex-direction: column;
  gap: 2px;
  background: #fff;

  &__label {
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: #888;
    font-weight: 500;
  }
  &__value {
    font-size: 18px;
    font-weight: 700;
  }

  &--rev {
    background: #e8f5e9;
    border-left: 3px solid #2e7d32;
  }
  &--spent {
    background: #fef2f2;
    border-left: 3px solid #c62828;
  }
  &--orders {
    background: #e3f2fd;
    border-left: 3px solid #1565c0;
  }
  &--roas-good {
    background: #e8f5e9;
    border-left: 3px solid #2e7d32;
  }
  &--roas-ok {
    background: #fff8e1;
    border-left: 3px solid #f57f17;
  }
  &--roas-bad {
    background: #fef2f2;
    border-left: 3px solid #c62828;
  }
}

/* Capture-only user banner */
.capture-user-banner {
  display: flex;
  align-items: center;
  gap: 12px;
  background: linear-gradient(135deg, #eef1ff 0%, #faf8ff 100%);
  border: 1px solid #c5cae9;
  border-radius: 10px;
  padding: 10px 14px;

  &__avatar {
    width: 36px;
    height: 36px;
    border-radius: 50%;
    background: linear-gradient(135deg, #3f51b5 0%, #7c4dff 100%);
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    box-shadow: 0 3px 8px rgba(63, 81, 181, 0.25);
  }

  &__text {
    flex: 1;
    min-width: 0;
    line-height: 1.25;
  }

  &__name {
    font-size: 14px;
    font-weight: 700;
    color: #2c3e7a;
  }

  &__email {
    font-size: 12px;
    color: #666;
    word-break: break-all;
  }

  &__meta {
    text-align: right;
    line-height: 1.3;
    flex-shrink: 0;
  }

  &__date {
    font-size: 12px;
    font-weight: 600;
    color: #3f51b5;
    display: inline-flex;
    align-items: center;
    gap: 4px;
  }

  &__time {
    font-size: 11px;
    color: #888;
    display: inline-flex;
    align-items: center;
    gap: 4px;
    margin-top: 2px;
  }
}

/* Tables */
.catalog-card.capturing {
  .table-scroll-wrapper {
    max-height: none !important;
    overflow: visible !important;
  }
  .v-pagination,
  .no-capture {
    display: none !important;
  }
}

/* ===== Capture overlay =====
   Full-screen mask shown while DOM is laid out off-screen for html2canvas,
   so the user never sees a flash / blank gap on the page. */
.capture-overlay {
  position: fixed;
  inset: 0;
  background: rgba(20, 24, 56, 0.55);
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;

  &__card {
    background: #ffffff;
    border-radius: 16px;
    padding: 28px 36px 24px;
    box-shadow: 0 24px 60px rgba(0, 0, 0, 0.25);
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    min-width: 320px;
    max-width: 360px;
    animation: captureCardPop 0.32s cubic-bezier(0.34, 1.56, 0.64, 1);
  }

  &__spinner {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 14px;
  }

  &__camera {
    position: absolute;
    animation: capturePulse 1.4s ease-in-out infinite;
  }

  &__title {
    font-size: 16px;
    font-weight: 700;
    color: #2c3e7a;
    line-height: 1.3;
  }

  &__subtitle {
    font-size: 12.5px;
    color: #888;
    margin-top: 4px;
    line-height: 1.45;
  }

  &__progress {
    margin-top: 16px;
    width: 100%;
    height: 4px;
    background: #eef0fb;
    border-radius: 999px;
    overflow: hidden;
  }

  &__progress-bar {
    width: 40%;
    height: 100%;
    background: linear-gradient(90deg, #3f51b5, #7c4dff);
    border-radius: 999px;
    animation: captureSlide 1.3s ease-in-out infinite;
  }
}

@keyframes captureCardPop {
  from {
    transform: scale(0.85) translateY(8px);
    opacity: 0;
  }
  to {
    transform: scale(1) translateY(0);
    opacity: 1;
  }
}

@keyframes capturePulse {
  0%,
  100% {
    transform: scale(1);
    opacity: 0.85;
  }
  50% {
    transform: scale(1.18);
    opacity: 1;
  }
}

@keyframes captureSlide {
  0% {
    transform: translateX(-110%);
  }
  100% {
    transform: translateX(260%);
  }
}

/* Fade transition wrapper */
.capture-fade-enter-active,
.capture-fade-leave-active {
  transition: opacity 0.22s ease;
}
.capture-fade-enter,
.capture-fade-leave-to {
  opacity: 0;
}

/* ===== Capture frame =====
   While exporting, lock the left content to a fixed pixel width and render it
   off-screen so html2canvas always paints the same layout — independent of the
   user's browser / device / viewport / zoom level. */
.catalog-capture-frame {
  position: fixed !important;
  top: 0 !important;
  left: -20000px !important;
  width: 1280px !important;
  min-width: 1280px !important;
  max-width: 1280px !important;
  margin: 0 !important;
  padding: 20px !important;
  background: #f4f6fb !important;
  box-sizing: border-box !important;
  z-index: 0;
  transform: none !important;

  /* No scroll containers — every table / card must expand to full content */
  .table-scroll-wrapper,
  .unmapped-table-wrap {
    max-height: none !important;
    overflow: visible !important;
  }

  ::v-deep .v-data-table__wrapper {
    overflow: visible !important;
    max-height: none !important;
  }
}

.table-scroll-wrapper {
  overflow: visible;
  border-radius: 8px;

  ::v-deep .v-data-table__wrapper {
    overflow: visible;
  }

  ::v-deep thead th {
    background: #f0f4ff !important;
  }
}

.unmapped-table-wrap {
  overflow: auto;
  overscroll-behavior: contain;
  -webkit-overflow-scrolling: touch;
  max-height: 60vh;

  ::v-deep thead th {
    background: #fff3e0 !important;
    position: sticky;
    top: 0;
    z-index: 2;
  }
}

.report-table {
  border-radius: 8px;

  ::v-deep thead th {
    background: #f0f4ff !important;
    font-size: 12px !important;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: #3f51b5 !important;
    padding-top: 14px !important;
    padding-bottom: 14px !important;
  }

  ::v-deep tbody td {
    padding-top: 12px !important;
    padding-bottom: 12px !important;
    font-size: 14px !important;
  }

  ::v-deep tbody tr:hover {
    background: #f8f9ff !important;
  }
}

.unmapped-table {
  ::v-deep thead th {
    background: #fff3e0 !important;
    color: #e65100 !important;
  }
}

/* Period label next to a catalog card title (e.g. the day / month) */
.catalog-card__period {
  font-size: 12px;
  font-weight: 600;
  color: #9094a8;
  margin-left: 8px;
  white-space: nowrap;
}

/* Monthly catalog card: purple accent to distinguish from the daily table */
.catalog-card--month {
  border: 1px solid #e3d9ff;
  background: linear-gradient(135deg, #fbf9ff 0%, #ffffff 100%);
}

.report-table--month {
  ::v-deep thead th {
    background: #f3edff !important;
    color: #5e35b1 !important;
  }
  ::v-deep tbody tr:hover {
    background: #f7f3ff !important;
  }
}

/* Cell helpers */
.cell-clamp {
  max-width: 100%;
  font-size: 13px;
  line-height: 1.4;
  white-space: pre-wrap;
  word-break: break-word;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  cursor: default;
}

.order-cell {
  display: inline-block;
  text-align: center;
  font-size: 16px;
  font-weight: 700;
  color: #1565c0;
  background: #e3f2fd;
  border-radius: 8px;
  padding: 4px 14px;
  min-width: 60px;
  line-height: 1.4;

  &__num {
    line-height: 1.4;
  }
}

/* ===== Note cell ===== */
.note-cell {
  padding: 6px 0;
  width: 100%;
}

/* Display mode */
.note-display-wrap {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  width: 100%;
}

.note-display {
  flex: 1;
  min-width: 0;
  background: #f8f9ff;
  border: 1px dashed #d6d9ee;
  border-radius: 8px;
  padding: 6px 10px;
  font-size: 13px;
  line-height: 1.45;
  color: #2c3e7a;
  cursor: text;
  white-space: pre-wrap;
  word-break: break-word;
  transition: background 0.15s, border-color 0.15s;

  &:hover {
    background: #eef0fb;
    border-color: #3f51b5;
  }

  &--empty {
    background: transparent;
    border-style: dashed;
    border-color: #d6d9ee;
  }

  &__placeholder {
    color: #9aa3b2;
    font-style: italic;
    font-size: 12px;
  }
}

/* Edit mode */
.note-edit {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  width: 100%;

  .note-input {
    flex: 1;
    font-size: 13px;

    ::v-deep .v-input__slot {
      padding: 6px 10px !important;
    }
    ::v-deep textarea {
      font-size: 13px;
      line-height: 1.45;
    }
    ::v-deep fieldset {
      border-color: #3f51b5 !important;
      border-width: 2px !important;
    }
  }

  &__actions {
    display: flex;
    flex-direction: column;
    gap: 2px;
    padding-top: 2px;
  }
}

.note-action-btn {
  transition: transform 0.15s ease, background 0.15s ease;

  &:hover {
    transform: scale(1.12);
  }
}

/* Capture mode: remove all interactive styling so note prints clean */
.catalog-card.capturing {
  .report-table ::v-deep td {
    vertical-align: top !important;
    padding-top: 12px !important;
    padding-bottom: 12px !important;
  }

  .note-display {
    background: transparent;
    border: none;
    padding: 0;
    color: #1a237e;
    font-size: 12.5px;
    line-height: 1.45;
    cursor: default;
    white-space: pre-wrap;
    word-break: break-word;
    overflow: visible;
  }

  .note-display--empty {
    color: #aaa;
    font-style: italic;
  }
}

@keyframes fadeInScale {
  from {
    opacity: 0;
    transform: scale(0.6);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

/* ===== Bottom summary cards ===== */
/* ===== Plan card ===== */
.plan-card {
  border: 1px solid #e3e7f5;
  background: linear-gradient(135deg, #fdfcff 0%, #f6f8ff 100%);
  transition: border-color 0.2s, box-shadow 0.2s;

  &__icon {
    width: 38px;
    height: 38px;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: linear-gradient(135deg, #3f51b5 0%, #7c4dff 100%);
    box-shadow: 0 4px 10px rgba(63, 81, 181, 0.25);
  }

  &__title {
    font-size: 15px;
    font-weight: 700;
    color: #2c3e7a;
    line-height: 1.2;
  }

  &__subtitle {
    font-size: 12px;
    color: #888;
    margin-top: 2px;
    display: inline-flex;
    align-items: center;
    gap: 4px;
  }

  &__dirty-tag {
    color: #c62828;
    font-weight: 600;
    font-size: 11px;
  }

  &__saved-tag {
    color: #2e7d32;
    font-weight: 600;
    font-size: 11px;
  }

  &__counter {
    font-size: 11px;
    color: #888;
    font-variant-numeric: tabular-nums;
    background: #f0f2f8;
    border-radius: 6px;
    padding: 3px 8px;
    font-weight: 500;

    &--warn {
      color: #c62828;
      background: #fef2f2;
    }
  }

  &__saved-flash {
    display: inline-flex;
    align-items: center;
    color: #2e7d32;
    font-size: 13px;
    font-weight: 500;
    background: #e8f5e9;
    border: 1px solid #c8e6c9;
    border-radius: 8px;
    padding: 6px 12px;
  }

  &--dirty {
    border-color: #3f51b5;
    box-shadow: 0 0 0 3px rgba(63, 81, 181, 0.08);
  }
}

.plan-textarea {
  ::v-deep .v-input__slot {
    padding: 10px 14px !important;
    background: #ffffff !important;
  }
  ::v-deep textarea {
    font-size: 13px !important;
    line-height: 1.5 !important;
    color: #333 !important;
  }
  ::v-deep fieldset {
    border-color: #d6d9ee !important;
  }
  &.v-input--is-focused {
    ::v-deep fieldset {
      border-color: #3f51b5 !important;
      border-width: 2px !important;
    }
  }
}

/* Plan display (capture mode) — plain text, no textarea quirks */
.plan-display {
  background: #ffffff;
  border: 1px solid #d6d9ee;
  border-radius: 6px;
  padding: 12px 14px;
  font-size: 13px;
  line-height: 1.55;
  color: #333;
  white-space: pre-wrap;
  word-break: break-word;
  min-height: 80px;

  &__placeholder {
    color: #aaa;
    font-style: italic;
  }
}

.summary-card {
  border: 1px solid #e3e7f5;

  &__icon {
    width: 38px;
    height: 38px;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;

    &--day {
      background: linear-gradient(135deg, #3f51b5 0%, #5c6bc0 100%);
    }
    &--month {
      background: linear-gradient(135deg, #7c4dff 0%, #b388ff 100%);
    }
  }

  &__title {
    font-size: 15px;
    font-weight: 700;
    color: #2c3e7a;
    line-height: 1.2;
  }

  &__subtitle {
    font-size: 12px;
    color: #888;
    margin-top: 2px;
  }

  &--day {
    background: linear-gradient(135deg, #f8f9ff 0%, #ffffff 100%);
  }
  &--month {
    background: linear-gradient(135deg, #f3edff 0%, #fbf8ff 100%);
    border-color: #d9ccff;
  }

  /* Compact (side-by-side) variant */
  &--compact {
    .summary-card__icon--sm {
      width: 26px;
      height: 26px;
      border-radius: 7px;
    }
    .summary-card__title--sm {
      font-size: 13px;
    }
    .summary-card__subtitle {
      font-size: 11px;
      margin-top: 0;
    }
    .summary-card__head-text {
      min-width: 0;
    }
  }
}

.summary-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 8px;
}

/* Compact grid: 4 items on a single row */
.summary-grid--compact {
  grid-template-columns: repeat(4, 1fr);
  gap: 6px;

  .summary-cell {
    padding: 6px 8px;
    gap: 2px;
    min-width: 0;

    &__label {
      font-size: 10px;
      letter-spacing: 0.3px;
    }
    &__value {
      font-size: 13px;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }
}

@media (max-width: 599px) {
  .summary-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

.summary-cell {
  background: #fff;
  border-radius: 8px;
  padding: 10px 12px;
  border: 1px solid #eef0fb;
  display: flex;
  flex-direction: column;
  gap: 4px;

  &__label {
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: #888;
    font-weight: 600;
  }
  &__value {
    font-size: 18px;
    font-weight: 700;
    font-variant-numeric: tabular-nums;
  }

  &--rev {
    background: #f1f8f1;
    border-color: #c8e6c9;
    .summary-cell__value {
      color: #2e7d32;
    }
  }
  &--spent {
    background: #fef2f2;
    border-color: #ffd2d2;
    .summary-cell__value {
      color: #c62828;
    }
  }
  &--orders {
    background: #e3f2fd;
    border-color: #bbdefb;
    .summary-cell__value {
      color: #1565c0;
    }
  }
}

.totals-chip--roas-good,
.summary-cell.totals-chip--roas-good {
  background: #f1f8f1;
  border-color: #c8e6c9;
}
.totals-chip--roas-ok,
.summary-cell.totals-chip--roas-ok {
  background: #fff8e1;
  border-color: #ffe0a3;
}
.totals-chip--roas-bad,
.summary-cell.totals-chip--roas-bad {
  background: #fef2f2;
  border-color: #ffd2d2;
}

/* Monthly summary: distinguishable palette vs Daily */
.summary-card--month {
  .summary-cell {
    background: #ffffff;
    border-color: #e3d9ff;

    &--rev {
      background: #ecf7ee;
      border-color: #b8dcc0;
      .summary-cell__value {
        color: #1b5e20;
      }
    }
    &--spent {
      background: #fdecec;
      border-color: #f3bfbf;
      .summary-cell__value {
        color: #8e1f1f;
      }
    }
    &--orders {
      background: #ede7ff;
      border-color: #cbb9ff;
      .summary-cell__value {
        color: #4527a0;
      }
    }

    &.totals-chip--roas-good {
      background: #ecf7ee;
      border-color: #b8dcc0;
    }
    &.totals-chip--roas-ok {
      background: #fff3d6;
      border-color: #f3d28a;
    }
    &.totals-chip--roas-bad {
      background: #fdecec;
      border-color: #f3bfbf;
    }
  }
}

.catalog-thumb-wrap {
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.catalog-thumb {
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  background: #fff;
}

.catalog-thumb-placeholder {
  width: 48px;
  height: 48px;
  border: 1px dashed #c5cae9;
  border-radius: 6px;
  background: #f5f7ff;
  display: flex;
  align-items: center;
  justify-content: center;
}

.campaign-id-cell {
  font-family: 'JetBrains Mono', 'Menlo', monospace;
  font-size: 12px;
  color: #3f51b5;
  cursor: pointer;
  word-break: break-all;

  &:hover {
    text-decoration: underline;
  }
}

.kbd-chip {
  display: inline-flex;
  align-items: center;
  font-family: 'JetBrains Mono', 'Menlo', monospace;
  font-size: 11px;
  background: #eef0fb;
  color: #3f51b5;
  border-radius: 4px;
  padding: 2px 6px;
  cursor: pointer;
  border: 1px solid #d6d9ee;

  &:hover {
    background: #e3e7f5;
  }

  &__label {
    font-family: 'Roboto', sans-serif;
    color: #6b7280;
    font-weight: 600;
    margin-right: 4px;
    flex-shrink: 0;
  }

  &__value {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    min-width: 0;
  }

  &--truncate {
    max-width: 180px;
    overflow: hidden;
  }
}

.date-chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  color: #555;
  background: #f5f5f5;
  border-radius: 4px;
  padding: 2px 6px;
}

/* Account block */
.account-block {
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-size: 12px;
  padding: 4px 0;
  line-height: 1.4;

  &__name {
    font-weight: 700;
    color: #3f51b5;
    font-size: 13px;
    display: inline-flex;
    align-items: center;
  }
  &__id {
    display: inline-flex;
    align-items: center;
    cursor: pointer;
    width: fit-content;

    &:hover {
      opacity: 0.85;
    }
  }
  &__person {
    color: #333;
    font-weight: 500;
  }
  &__email {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    color: #888;
    font-size: 11px;
    word-break: break-all;
  }
}

/* Ads block */
.ads-block {
  display: flex;
  gap: 12px;
  padding: 4px 0;
  align-items: flex-start;

  &__media {
    flex-shrink: 0;
  }

  &__thumb-wrap {
    position: relative;
    width: 56px;
    height: 56px;
    border-radius: 8px;
    overflow: hidden;
    display: inline-block;
    border: 1px solid #e0e0e0;
    background: #fafafa;
    text-decoration: none;
  }

  &__thumb {
    width: 56px;
    height: 56px;
  }

  &__thumb-placeholder {
    width: 56px;
    height: 56px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #f5f7ff;
  }

  &__link-icon {
    position: absolute;
    bottom: 2px;
    right: 2px;
    background: rgba(0, 0, 0, 0.55);
    border-radius: 50%;
    padding: 2px;
  }

  &__info {
    display: flex;
    flex-direction: column;
    gap: 3px;
    min-width: 0;
    flex: 1;
  }

  &__campaign {
    font-weight: 600;
    color: #3f51b5;
    font-size: 13px;
    line-height: 1.35;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    word-break: break-word;
  }

  &__adname,
  &__adcontent {
    font-size: 12px;
    color: #555;
    display: -webkit-box;
    -webkit-line-clamp: 1;
    line-clamp: 1;
    -webkit-box-orient: vertical;
    overflow: hidden;
    word-break: break-word;

    .v-icon {
      margin-right: 4px;
    }
  }

  &__adcontent {
    color: #888;
  }

  &__meta {
    display: flex;
    gap: 6px;
    margin-top: 2px;
    flex-wrap: wrap;
  }
}

/* Stats block */
.stats-block {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.stat-pill {
  display: inline-flex;
  align-items: center;
  border-radius: 8px;
  padding: 6px 10px;
  font-size: 12px;
  background: #f5f7ff;
  border: 1px solid #e3e7f5;
  gap: 6px;

  &__label {
    color: #888;
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.4px;
  }
  &__value {
    margin-left: auto;
    font-weight: 700;
    font-size: 13px;
    font-variant-numeric: tabular-nums;
  }

  &--clicks {
    background: #e3f2fd;
    border-color: #bbdefb;
    .stat-pill__value {
      color: #1565c0;
    }
  }
  &--spend {
    background: #fef2f2;
    border-color: #ffd2d2;
    .stat-pill__value {
      color: #c62828;
    }
  }
}

.info-banner {
  display: flex;
  align-items: center;
  background: #fff8e1;
  color: #6d4c00;
  padding: 8px 12px;
  border-radius: 8px;
  font-size: 13px;
  border: 1px solid #ffe0a3;
}

/* ===== Map Dialog ===== */
.map-dialog-card {
  border-radius: 12px;
}

.campaign-summary {
  background: #f5f7ff;
  border: 1px solid #e3e7f5;
  border-radius: 8px;
  padding: 12px 14px;
  display: flex;
  flex-direction: column;
  gap: 6px;

  &__row {
    display: flex;
    align-items: baseline;
    gap: 8px;
    font-size: 13px;

    .label {
      min-width: 130px;
      color: #888;
      text-transform: uppercase;
      font-size: 11px;
      letter-spacing: 0.5px;
    }
    .value {
      word-break: break-all;
      font-weight: 500;
    }
  }
}

.map-dialog-list {
  max-height: 50vh;
  overflow-y: auto;
}

.product-row {
  border-bottom: 1px solid #f0f2f8;
  cursor: pointer;
  transition: background 0.15s;

  &:hover {
    background: #f8f9ff;
  }

  &--selected {
    background: #e8eaf6 !important;
  }
}

.product-thumb-wrap {
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.product-thumb {
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  background: #fff;
}
.product-thumb-placeholder {
  width: 48px;
  height: 48px;
  border: 1px dashed #c5cae9;
  border-radius: 6px;
  background: #f5f7ff;
  display: flex;
  align-items: center;
  justify-content: center;
}

.selected-product-pill {
  display: inline-flex;
  align-items: center;
  background: #e8f5e9;
  color: #2e7d32;
  border-radius: 999px;
  padding: 4px 10px;
  font-size: 12px;
  font-weight: 600;
  max-width: 60%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Mobile responsive */
@media (max-width: 599px) {
  .totals-chip__value {
    font-size: 15px;
  }
  .campaign-summary__row {
    flex-direction: column;
    align-items: flex-start;
    .label {
      min-width: 0;
    }
  }
}
</style>
